import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Typography } from '@ocx/ui';
import { QRCodeIcon } from '@ocx/ui-icons';

import { messages } from './loyalty-id-barcode-scan-action-sheet.messages';

type LoyaltyIdBarcodeScanButtonProps = {
  onClick?: () => void;
};

const BUTTON_SIZE_PX = 56;
const EXPANDED_BUTTON_WIDTH_PX = 148;
const BUTTON_BORDER_RADIUS_PX = BUTTON_SIZE_PX / 2;
const EXPANDED_LABEL_WIDTH_PX = 96;
const SCAN_ICON_SIZE_PX = 24;

const BUTTON_EXPANDED_GAP = 1;
const BUTTON_RIGHT_OFFSET = 1;
const BUTTON_BOTTOM_OFFSET = 2;
// BottomNavigation is 56px here (MUI default). Our theme override
// (`apps/frontend/src/theme/createComponentsOverrides.ts`) customizes colors/layout,
// but does not define/export a shared height token, and MUI does not expose this
// value as an importable runtime constant. Keep this local until we introduce
// a shared app layout constant and use it in both BottomNavigation styles and this offset.
const BOTTOM_NAVIGATION_HEIGHT_PX = 56;

const COLLAPSE_OFFSET_PX = 24;

export const LoyaltyIdBarcodeScanButton = ({ onClick }: LoyaltyIdBarcodeScanButtonProps) => {
  const [isCollapsedOnScroll, setIsCollapsedOnScroll] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsCollapsedOnScroll(false);
      return;
    }

    const getTargetScrollTop = (target?: EventTarget | null) => {
      if (target instanceof HTMLElement) {
        return target.scrollTop;
      }

      return window.scrollY ?? window.pageYOffset ?? document.documentElement.scrollTop ?? 0;
    };

    const handleScroll = (event?: Event) => {
      const currentScroll = getTargetScrollTop(event?.target);
      setIsCollapsedOnScroll(currentScroll > COLLAPSE_OFFSET_PX);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        pointerEvents: 'none',
        right: (theme) => theme.spacing(BUTTON_RIGHT_OFFSET),
        bottom: (theme) =>
          `calc(var(--ion-safe-area-bottom, 0px) + ${BOTTOM_NAVIGATION_HEIGHT_PX}px + ${theme.spacing(
            BUTTON_BOTTOM_OFFSET,
          )})`,
      }}>
      <Button
        onClick={onClick}
        sx={{
          minWidth: BUTTON_SIZE_PX,
          width: isCollapsedOnScroll ? BUTTON_SIZE_PX : EXPANDED_BUTTON_WIDTH_PX,
          height: BUTTON_SIZE_PX,
          borderRadius: BUTTON_BORDER_RADIUS_PX,
          pointerEvents: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          gap: isCollapsedOnScroll ? 0 : BUTTON_EXPANDED_GAP,
          bgcolor: 'primary.main',
          boxShadow: (theme) => theme.shadows[8],
          zIndex: (theme) => Math.min(theme.zIndex.fab + 1, theme.zIndex.modal - 1),
          transition: (theme) =>
            theme.transitions.create(['width', 'gap'], {
              duration: theme.transitions.duration.short,
            }),
          textTransform: 'none',
        }}>
        <Typography
          variant="h6"
          component="span"
          sx={(theme) => ({
            display: 'block',
            width: isCollapsedOnScroll ? 0 : EXPANDED_LABEL_WIDTH_PX,
            opacity: isCollapsedOnScroll ? 0 : 1,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            transition: theme.transitions.create(['width', 'opacity'], {
              duration: theme.transitions.duration.short,
            }),
          })}>
          <FormattedMessage {...messages.openButtonLabel} />
        </Typography>
        <QRCodeIcon sx={{ fontSize: SCAN_ICON_SIZE_PX }} />
      </Button>
    </Box>
  );
};
